import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Loader2 } from "lucide-react";
import type { AccessRequestRow } from "./accessRequestTypes";

const API_URL = process.env.REACT_APP_API_URL || "";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface Props {
  request: AccessRequestRow | null;
  onOpenChange: (open: boolean) => void;
  onApproved: (emailOutcome: string) => void;
}

/**
 * Approve dialog (ticket 6): what will happen (create vs join), the e-mail the
 * letter goes to — prefilled with the submitted address and editable, the one
 * place a typo or a taken provider e-mail is fixed — and the compare-and-swap
 * status the admin is looking at.
 */
export function ApproveRequestDialog({ request, onOpenChange, onApproved }: Props) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (request) {
      setEmail(request.decisionEmail ?? request.email);
      setError("");
    }
  }, [request]);

  if (!request) return null;

  const snapshot = request.registrySnapshot;
  const inSystem = snapshot?.inSystem;
  const name = snapshot?.company?.name || request.companyName;
  const summaryKey =
    request.role === "BUILDING"
      ? inSystem?.buildingId ? "willJoinBuilding" : "willCreateBuilding"
      : inSystem?.providerId ? "willJoinProvider" : "willCreateProvider";
  const changed = email.trim().toLowerCase() !== request.email.trim().toLowerCase();
  const canSubmit = EMAIL_RE.test(email.trim()) && !submitting;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const body: { expectedStatus: string; email?: string } = { expectedStatus: request.status };
      if (changed) body.email = email.trim();
      const res = await axios.post<{ success: boolean; data: { emailOutcome: string } }>(
        `${API_URL}/api/admin/access-requests/${request.id}/approve`,
        body,
        { withCredentials: true }
      );
      onOpenChange(false);
      onApproved(res.data.data.emailOutcome);
    } catch (err: any) {
      const code = err.response?.data?.code as string | undefined;
      setError(t(`admin.accessRequests.errors.${code && KNOWN.has(code) ? code : "generic"}`));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!request} onOpenChange={(o) => { if (!submitting) onOpenChange(o); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("admin.accessRequests.dialog.title")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-foreground">{t(`admin.accessRequests.dialog.${summaryKey}`, { name })}</p>
          <div className="text-xs text-muted-foreground font-mono">{request.registryCode} · {request.personalCode}</div>
          <div className="space-y-1">
            <Label htmlFor="approve-email">{t("admin.accessRequests.dialog.emailLabel")}</Label>
            <Input id="approve-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            {changed && (
              <p className="text-xs text-muted-foreground">
                {t("admin.accessRequests.dialog.submittedEmail", { email: request.email })}
              </p>
            )}
          </div>
          {error && (
            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-lg border border-destructive/20">
              {error}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t("admin.accessRequests.dialog.cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit} data-confirm="approve">
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {t("admin.accessRequests.dialog.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const KNOWN = new Set([
  "already_decided", "registry_not_affiliated", "registry_unavailable", "role_conflict", "provider_email_taken", "invalid_email",
]);
